/**
 * Centralised POI selection state.
 * Single source of truth for which POIs are selected.
 * Automatically persists selected IDs to localStorage on every change.
 */

const SELECTION_KEY = "crp_poi_selection";

/** In-memory set of selected POI IDs. */
const _ids = new Set<string>(loadFromStorage());

/** Fired on every selection change (add/remove/clear). Does NOT fire on restore. */
let _onChange: (() => void) | null = null;

function loadFromStorage(): string[] {
  try {
    const raw = localStorage.getItem(SELECTION_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

export function isPoiSelected(id: string): boolean {
  return _ids.has(id);
}

export function togglePoiSelected(id: string): void {
  if (_ids.has(id)) _ids.delete(id);
  else               _ids.add(id);
  persistAndNotify();
}

export function clearSelectedPois(): void {
  _ids.clear();
  persistAndNotify();
}

/**
 * Restore selection from a previously-saved array.
 * Updates in-memory state and persists — but does NOT fire _onChange
 * (the caller decides whether to trigger a re-render).
 */
export function restoreSelectedPois(ids: string[]): void {
  _ids.clear();
  for (const id of ids) _ids.add(id);
  persistAndNotify();
}

export function getSelectedPoiIds(): string[] {
  return [..._ids];
}

/** Register a callback fired on every user-driven selection change. */
export function setOnSelectionChange(cb: () => void): void {
  _onChange = cb;
}

function persistAndNotify(): void {
  try {
    localStorage.setItem(SELECTION_KEY, JSON.stringify([..._ids]));
  } catch {
    // quota / private browsing — ignore
  }
  _onChange?.();
}
